/**
 * Collection browser (CB-5a) — the shared orchestrator every collection
 * surface consumes. The template picker donated the frame and choreography;
 * My Projects donated the card hover behaviour and multi-select. This module
 * NEVER mutates caller data: it renders, filters, selects, announces and
 * paints busy chrome; it emits intents and the SURFACE performs the
 * delete/restore/rename and owns the rollback (`bulkIntents[].perform` is a
 * caller-owned operation the module awaits purely to paint busy chrome).
 *
 * MUST NOT import ui/shell.js — enforced by collection-import-boundary-tests.
 * Toast/confirm reach surfaces through their own imports; this module needs
 * neither.
 *
 * Filtering is IN-PLACE over a registry (`_entries`), never a rebuild: writes
 * only `el.hidden`, and only when it changes; the <img> is never touched.
 * Rebuild happens on exactly three triggers: initial load, setRecords,
 * refresh. (Rebuilding cost 224ms of blocked main thread per keystroke and
 * re-requested 5.98MB of thumbnails mid-word.)
 */

import {
  createModal,
  createButton,
  createCheckbox,
  setButtonBusy,
  buildContextLine
} from './components.js';
import { openPopoverMenu } from './popover-menu.js';
import { iconEl } from './icons.js';
import { createDelayedLoader, renderSkeletonGrid, renderSkeletonRows } from './feedback.js';
import { debounce } from '../shared/timing.js';
import { buildSearchText, matchesQuery } from '../shared/collection-filter.js';
import { buildChipModel } from '../shared/collection-chips.js';
import { createCollectionSelection } from './collection-selection-model.js';
import {
  buildCollectionShell,
  renderChipRow,
  paintEmptyState,
  buildQueueStrip,
  paintQueueStrip
} from './collection-chrome.js';
import { createSlotQueue } from './slot-queue.js';
import {
  renderCollectionItem,
  updateCollectionItem,
  ACTION_ACTIVATE,
  ACTION_TOGGLE
} from './collection-item-view.js';
import { createTwoViewStage } from './collection-views.js';
import { applyStagger, armSettle } from './collection-stagger.js';
import { createThumbnailPool } from './collection-thumbnails.js';
import { gridKeyNav } from './grid-key-nav.js';
import { nextFocusTarget } from './section-list-interaction.js';
import { normalizeTiles, renderCollectionTile } from './collection-tile-view.js';
import { reportError } from '../core/report-error.js';

let _discloseSeq = 0;
let _popoverSeq = 0;

/** The card sits this far to the right of a row it is anchored beside. */
const POPOVER_BESIDE_GAP = 12;
/** …and this much above the row's top edge, so the header lines up with the row. */
const POPOVER_BESIDE_NUDGE = 8;
/** A caller with no trigger and no anchor gets the frame's top-left gutter. */
const POPOVER_FALLBACK_INSET = 16;

/**
 * Remembered list size for skeleton counts — project-history-modal's
 * `skeletonCount()` generalized: a fixed 6 made a twelve-project modal jump
 * its full height on every open. Clamped so a corrupted or absurd stored
 * value cannot paint a one-card or thousand-card skeleton.
 */
export function rememberedCount(key, { min = 4, max = 24, fallback = 6 } = {}) {
  let stored = NaN;
  try {
    stored = Number(localStorage.getItem(key));
  } catch {
    /* private mode / disabled storage — fall through to the fallback */
  }
  if (!Number.isFinite(stored) || stored <= 0) return fallback;
  return Math.min(max, Math.max(min, Math.round(stored)));
}

export function rememberCount(key, count) {
  try {
    localStorage.setItem(key, String(count));
  } catch {
    /* private mode — remembering is best-effort */
  }
}

/**
 * The engine, rendering into a caller-owned container (CB-12a).
 *
 * `openCollectionBrowser` is this function plus a modal: it creates the kit,
 * mounts here into `kit.body`, and hands back the same handle. Everything that
 * is not the dialog — the shell, filtering, selection, thumbnails, the three
 * delegated listeners, the two-view stage and the ordered teardown — lives
 * here and knows nothing about overlays.
 *
 * The modal reaches in through exactly three options, which is the whole
 * coupling the split removes:
 *  · `selbarHost` — the floating bar anchors to the MODAL CARD, not the
 *    scroller, because its strip is reserved in the scroller's padding. An
 *    embedded mount has no card, so it defaults to the container.
 *  · `onRequestClose` — `close()` is a REQUEST. The dialog owns the animation
 *    and calls back into `destroy()` through its own onClose, so backdrop,
 *    Escape, × and programmatic close all keep landing on one path. Without a
 *    host, close() tears down directly.
 *  · `apiExtras` — merged into the handle BEFORE `onOpen` runs, because
 *    surfaces build their foreign chrome against `api.body`.
 */
export function mountCollectionList(container, options) {
  const opts = options || {};
  if (!container) throw new Error('mountCollectionList requires a container');
  if (typeof opts.load !== 'function') throw new Error('mountCollectionList requires load()');
  if (typeof opts.adapt !== 'function') throw new Error('mountCollectionList requires adapt()');

  const layout = opts.layout || 'grid';
  const isRows = layout === 'rows';
  const idOf = opts.idOf || ((record, i) => String(record?.id ?? i));
  const searchOpts = opts.search === false ? false : { ...(opts.search || {}) };
  const chipOpts = opts.chips === false ? false : opts.chips ? { ...opts.chips } : false;
  const selOpts = opts.selection || null;

  // ── lifecycle state ──────────────────────────────────────────────────────
  let closed = false;
  let runId = 0;
  const openAbort = new AbortController();
  let runAbort = null;
  const cleanups = [];
  const observers = new Set();
  let settleCancel = null;
  let detailTeardown = null;
  let searchDebounce = null;
  let drawerOpen = false;
  let readySettled = false;
  let resolveReady;
  const ready = new Promise((resolve) => {
    resolveReady = resolve;
  });

  let records = [];
  /** @type {Map<string, {item: object, record: object, el: HTMLElement, titleEl: HTMLElement, metaEl: HTMLElement|null, posterImg: HTMLImageElement|null}>} */
  const _entries = new Map();
  let query = searchOpts !== false ? searchOpts.initialQuery || '' : '';
  let chipKey = chipOpts !== false ? chipOpts.activeKey || 'all' : 'all';
  let loadError = null;
  let actionInFlight = false;

  // ── tiles (PP-0): leading/trailing NON-record items ─────────────────────
  // Built once, re-PARKED on every renderList (the chips.trailing precedent):
  // renderList wipes listEl, and rebuilding here would drop the dismiss
  // button's focus mid-rebuild. Never in `_entries` — a tile is not a record,
  // so selection, visibleIds, counts and the rename contract cannot see it.
  /** @type {Map<string, {spec: object, el: HTMLElement, side: 'leading'|'trailing', primaryBtn: HTMLElement, dismissBtn: HTMLElement|null, pending: boolean}>} */
  const _tiles = new Map();
  let tileSpecs = normalizeTiles(opts.tiles);

  function buildTiles() {
    _tiles.clear();
    for (const side of ['leading', 'trailing']) {
      for (const spec of tileSpecs[side]) {
        const refs = renderCollectionTile(spec, {
          layout,
          itemTag: opts.itemTag || (opts.listTag === 'div' ? 'div' : 'li'),
          presentation: opts.listRole === 'listbox'
        });
        _tiles.set(spec.id, { spec, side, pending: false, ...refs });
      }
    }
  }

  function parkTiles() {
    const leading = [];
    const trailing = [];
    for (const tile of _tiles.values())
      (tile.side === 'leading' ? leading : trailing).push(tile.el);
    if (leading.length) listEl.prepend(...leading);
    if (trailing.length) listEl.append(...trailing);
  }

  function tileMatches(tile) {
    if (chipOpts !== false && chipKey !== 'all' && tile.spec.hideWhenChip) return false;
    if (!query) return true;
    return typeof tile.spec.matchesQuery === 'function' ? !!tile.spec.matchesQuery(query) : false;
  }

  /** The tile half of the filter pass; returns how many tiles are visible. */
  function applyTileFilter() {
    let visibleTiles = 0;
    for (const tile of _tiles.values()) {
      const match = tileMatches(tile);
      if (tile.el.hidden === match) tile.el.hidden = !match;
      if (match) visibleTiles += 1;
    }
    return visibleTiles;
  }

  function visibleNavigableEls() {
    return [...listEl.children].filter((el) => el.classList.contains('pb-coll-item') && !el.hidden);
  }

  function focusTargetFor(el) {
    if (!el) return null;
    const tile = el.dataset.cbTile && _tiles.get(el.dataset.cbTile);
    return tile?.primaryBtn || el;
  }

  /** Run a tile callback with the async contract: single-flight per tile,
   *  aria-busy on the container + the kit's busy on the primary, and a
   *  rejection routed through reportError so a failed paste is a toast, not
   *  a silent dead button. */
  async function runTileAction(tile, fn) {
    if (closed || tile.pending) return;
    tile.pending = true;
    tile.el.setAttribute('aria-busy', 'true');
    setButtonBusy(tile.primaryBtn, true, { busyLabel: null });
    try {
      await fn(api);
    } catch (err) {
      reportError('collection.tile', err, { user: 'toast' });
    } finally {
      tile.pending = false;
      if (tile.el.isConnected) {
        tile.el.removeAttribute('aria-busy');
        setButtonBusy(tile.primaryBtn, false);
      }
    }
  }

  /** Restore the dismissed control to its logical DOM neighbour. `index` is
   *  captured before the callback mutates/rebuilds tiles; after removal the
   *  next item occupies that same slot, whether it is a record or a tile. */
  function focusAfterDismiss(index) {
    const current = visibleNavigableEls();
    const target = focusTargetFor(current[index] || current[index - 1]);
    if (target) return target.focus();
    searchInput?.focus();
  }

  // ── disclosure (PP-0): the end-of-list reveal ────────────────────────────
  const discloseOpts =
    opts.disclosure && typeof opts.disclosure.hidden === 'function' ? opts.disclosure : null;
  let discloseEl = null;
  let discloseBtn = null;
  let revealInFlight = false;
  let lastRevealSucceeded = false;
  let pendingNewTimer = null;
  function discloseLabel(n) {
    const label = discloseOpts.label;
    return typeof label === 'function' ? String(label(n)) : String(label ?? 'Show more');
  }
  /** Visibility is DERIVED every paint, never latched: hidden() growing again
   *  (a chip change, new records) brings the row back. And it is shown only
   *  when at least one record is visible — at zero, the surface's empty-state
   *  action is the single reveal control, so a user never sees two. */
  function paintDisclosure(shown) {
    if (!discloseEl) return;
    const n = closed ? 0 : Math.max(0, Number(discloseOpts.hidden(api)) || 0);
    const show = n > 0 && shown > 0;
    discloseEl.hidden = !show;
    if (show) discloseBtn.textContent = discloseLabel(n);
    discloseBtn.setAttribute('aria-expanded', String(n === 0 && lastRevealSucceeded));
  }
  function markNewSince(snapshot) {
    const fresh = [];
    for (const [id, entry] of _entries) {
      const isNew = !snapshot.ids.has(id) || (!entry.el.hidden && !snapshot.visible.has(id));
      if (isNew && !entry.el.hidden) fresh.push(entry.el);
    }
    fresh.forEach((el, i) => {
      el.classList.add('pb-coll-item-new');
      el.style.setProperty('--pb-coll-idx', String(i));
      el.addEventListener('animationend', () => el.classList.remove('pb-coll-item-new'), {
        once: true
      });
    });
    if (pendingNewTimer) clearTimeout(pendingNewTimer);
    // Reduced motion never fires animationend; the class must not linger.
    pendingNewTimer = setTimeout(() => {
      for (const el of fresh) el.classList.remove('pb-coll-item-new');
      pendingNewTimer = null;
    }, 1000);
    return fresh;
  }
  async function runReveal() {
    if (closed || revealInFlight || !discloseOpts) return;
    revealInFlight = true;
    discloseBtn.setAttribute('aria-busy', 'true');
    setButtonBusy(discloseBtn, true, { busyLabel: null });
    const snapshot = { ids: new Set(_entries.keys()), visible: new Set(visibleIds()) };
    let ok = false;
    try {
      await discloseOpts.onReveal(api);
      ok = true;
    } catch (err) {
      reportError('collection.disclosure', err, { user: 'toast' });
    } finally {
      revealInFlight = false;
      if (!closed) {
        lastRevealSucceeded = ok;
        discloseBtn.removeAttribute('aria-busy');
        setButtonBusy(discloseBtn, false);
        paintDisclosure(visibleIds().length);
        if (ok) {
          const fresh = markNewSince(snapshot);
          if (discloseEl.hidden && fresh[0]) fresh[0].focus({ preventScroll: false });
          else if (discloseBtn.isConnected && !discloseEl.hidden) discloseBtn.focus();
        }
      }
    }
  }

  /**
   * Supersede every continuation owned by the prior lifecycle run. A run id
   * prevents a stale completion from painting; the abort signal prevents the
   * obsolete work from continuing after it can no longer be used.
   */
  function beginRun({ needsSignal = true } = {}) {
    runId += 1;
    runAbort?.abort();
    runAbort = needsSignal ? new AbortController() : null;
    return { runId, signal: runAbort?.signal || openAbort.signal };
  }

  // CB-10: a SINGLE-select surface (page selection's "add one page" mode) has
  // exactly one row selected at all times, so a floating bulk bar would sit
  // there permanently saying so, over a strip reserved under a list that has
  // nothing to bulk-act on. `bar: false` drops both; the rows express the
  // selection and the surface's own footer carries the action.
  const isSingle = !!selOpts && selOpts.mode === 'single';
  const wantsBar = !!selOpts && selOpts.bar !== false && !isSingle;
  // Radios need a shared `name` to be one group — and it must be unique per
  // open, or a second dialog would join the first one's group.
  const radioGroupName = `pb-coll-radio-${opts.testid || 'collection'}`;

  // DS-1: the slot queue, promoted out of the image picker. Absent ⇒ nothing
  // below runs and the dialog is what it always was.
  const queueOpts = opts.queue || null;
  const queue = queueOpts
    ? createSlotQueue(queueOpts.slots, {
        noun: queueOpts.noun,
        nounPlural: queueOpts.nounPlural
      })
    : null;
  let queueRefs = null;

  const stage = createTwoViewStage(container);
  const chrome = buildCollectionShell({
    layout,
    listTag: opts.listTag || 'ul',
    listRole: opts.listRole || null,
    listAriaLabel: opts.listAriaLabel,
    search: searchOpts,
    chips: chipOpts,
    headerMeta: opts.headerMeta !== false,
    // The right-hand toolbar control (a sort order, a source). Passed straight
    // through: the engine never reads it, so what it filters or orders stays
    // entirely the surface's business.
    toolbarTrailing: opts.toolbarTrailing || null,
    density: opts.density,
    selection: !!selOpts,
    selectionBar: wantsBar
  });
  stage.listView.appendChild(chrome.shell);
  const {
    searchInput,
    metaEl,
    chipRow,
    drawerEl,
    scroller,
    listEl,
    emptyEl,
    trailingEl,
    selbar,
    liveRegion
  } = chrome;

  // Optional sticky-chip state belongs to the browser, not to a donor. The
  // chips sit above the inner scroller, so a one-pixel probe at the scroller's
  // leading edge is enough to paint the divider only after content moves
  // underneath them. The observer joins the shared teardown registry below.
  if (chipRow && chipOpts !== false && chipOpts.sticky) {
    chipRow.classList.add('pb-coll-chips-sticky');
    const sentinel = document.createElement('span');
    sentinel.className = 'pb-coll-chip-sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    scroller.prepend(sentinel);
    if (typeof IntersectionObserver === 'function') {
      const observer = new IntersectionObserver(
        ([entry]) => chipRow.setAttribute('data-stuck', String(!entry.isIntersecting)),
        { root: scroller, threshold: 0 }
      );
      observer.observe(sentinel);
      observers.add(observer);
    }
  }
  // The selection bar anchors to the MODAL CARD, not the scroller — its strip
  // is reserved in the scroller's padding unconditionally (collection.css).
  if (selbar) (opts.selbarHost || container).appendChild(selbar);

  if (discloseOpts) {
    discloseEl = document.createElement('div');
    discloseEl.className = 'pb-coll-disclose';
    discloseEl.hidden = true;
    discloseBtn = createButton({
      variant: 'outline',
      size: 'sm',
      // The kit's own pill hook, not a local radius. Every other standalone
      // control in this chrome — chips, the More button, the search field — is
      // a capsule; a 4px-radius button at the end of the list was the one
      // rectangle in the surface.
      pill: true,
      className: 'pb-coll-disclose-btn',
      ariaExpanded: false,
      dataset: { cbDisclose: 'true' },
      onClick: () => runReveal()
    });
    if (discloseOpts.controls) {
      if (!listEl.id) listEl.id = `pb-coll-list-${++_discloseSeq}`;
      discloseBtn.setAttribute('aria-controls', listEl.id);
    }
    discloseEl.appendChild(discloseBtn);
    // After the list, before the empty state: scrolls with the content.
    scroller.insertBefore(discloseEl, emptyEl);
  }
  buildTiles();

  // ── selection ────────────────────────────────────────────────────────────
  const selection = selOpts
    ? createCollectionSelection({
        getOrder: () => visibleIds(),
        getAllIds: () => [..._entries.keys()],
        canSelect: (id) => {
          const entry = _entries.get(id);
          if (!entry) return false;
          if (entry.item.canSelect === false) return false;
          if (typeof selOpts.canSelect === 'function') return selOpts.canSelect(entry.item);
          return true;
        },
        noun: selOpts.noun || 'item',
        nounPlural: selOpts.nounPlural
      })
    : null;

  // ── thumbnails ───────────────────────────────────────────────────────────
  const thumbPool = opts.thumbnails
    ? createThumbnailPool({
        resolve: opts.thumbnails.resolve,
        concurrency: opts.thumbnails.concurrency || 4,
        signal: openAbort.signal
      })
    : null;

  // ── core queries ─────────────────────────────────────────────────────────
  function entryMatches(entry) {
    if (chipOpts !== false && chipKey !== 'all') {
      const matches =
        typeof chipOpts.matches === 'function'
          ? chipOpts.matches(entry.record, chipKey)
          : chipOpts.keyOf
            ? chipOpts.keyOf(entry.record) === chipKey
            : true;
      if (!matches) return false;
    }
    // `filter` (CB-8): a surface that owns a visibility policy BEYOND the
    // query — Version History's run folding — replaces the default matching
    // wholesale. It receives the live query and composes its own semantics
    // (VH: a query suspends folding so a match inside a folded run is never
    // silently hidden).
    if (typeof opts.filter === 'function') return !!opts.filter(entry.record, { query });
    if (!query) return true;
    // `search.matches` (CB-5b): a surface-owned predicate for genuinely
    // bespoke query semantics — fleet's artifact queries ("nav-2445", a pack
    // key) are gated on a shape regex the generic tokenizer must not inherit.
    if (searchOpts !== false && typeof searchOpts.matches === 'function') {
      return !!searchOpts.matches(entry.record, query);
    }
    const haystack = entry.item.searchText ?? buildSearchText([entry.item.title, entry.item.meta]);
    return matchesQuery(haystack, query);
  }

  function visibleIds() {
    const out = [];
    for (const [id, entry] of _entries) if (!entry.el.hidden) out.push(id);
    return out;
  }

  // ── filtering (in place — writes ONLY el.hidden) ─────────────────────────
  function applyFilter() {
    if (closed) return;
    let shown = 0;
    for (const entry of _entries.values()) {
      const match = entryMatches(entry);
      if (entry.el.hidden === match) entry.el.hidden = !match;
      if (match) {
        shown += 1;
        updateCollectionItem(entry, entry.item, { query });
      }
    }
    const visibleTiles = applyTileFilter();
    // `shown` stays RECORDS-only: the meta line's "N total" must not count tiles.
    paintState(shown, { keepList: visibleTiles > 0 });
    paintDisclosure(shown);
    if (selection) syncSelectionUI();
    if (typeof opts.onFilter === 'function') opts.onFilter(api, { shown, query, chipKey });
    return shown;
  }

  function paintState(shown, { keepList = false } = {}) {
    paintEmptyState({
      emptyEl,
      metaEl,
      listEl,
      shown,
      total: _entries.size,
      query,
      chipKey,
      emptyCopy: opts.emptyCopy,
      filteredEmptyCopy: opts.filteredEmptyCopy,
      errorCopy: opts.errorCopy,
      error: !!loadError,
      metaText: opts.headerMetaText,
      illustration: opts.illustration,
      keepList
    });
  }

  // ── the slot queue (DS-1) ────────────────────────────────────────────────
  //
  // Promoted out of `editing/image-picker/session.js`, where the whole thing —
  // engine, chrome and the header takeover — lived inside one surface. The
  // engine is `ui/slot-queue.js`; the strip's structure is `collection-chrome`;
  // this is the wiring, which is the part that was never shared and is where
  // the accessibility gap was.

  /** Repaint the strip from the current cursor. Safe before install. */
  function paintQueue() {
    if (!queue || !queueRefs) return;
    const slot = queue.current();
    const state = {
      cursor: queue.cursor,
      size: queue.size,
      filledCount: queue.filledCount(),
      remaining: queue.remainingLabel()
    };
    paintQueueStrip(queueRefs, queue, {
      title:
        typeof queueOpts.title === 'function' ? queueOpts.title(slot, state) : slot?.label || '',
      sub: typeof queueOpts.sub === 'function' ? queueOpts.sub(slot, state) : '',
      buildChip: (s, i) => {
        const content = [];
        if (s.thumb) {
          const img = document.createElement('img');
          img.alt = '';
          img.src = s.thumb;
          content.push(img);
        }
        // Through the kit — `variant: 'bare'` because the chip carries its own
        // surface, exactly as `.pb-coll-chip` does. A hand-rolled <button>
        // fails QI-5's no-hand-rolled-button sweep, and rightly: it would skip
        // the kit's aria wiring and busy handling.
        return createButton({
          variant: 'bare',
          className: 'pb-coll-slot',
          role: 'tab',
          content,
          // The chip is a picture, so the NAME has to be words. "filled" is
          // stated rather than left to the check badge, which is decoration.
          ariaLabel: `${s.label}, ${s.filled ? 'filled' : 'empty'}`,
          dataset: { pbSlot: String(i) }
        });
      }
    });
  }

  /**
   * Move the cursor and repaint. `fromKeyboard` keeps focus on the strip;
   * a pointer selection hands focus to the search, which is where the next
   * thing a mouse user does happens.
   */
  function selectSlot(index, { fromKeyboard = false } = {}) {
    if (!queue || !queue.select(index)) return false;
    paintQueue();
    if (fromKeyboard) {
      queueRefs?.strip.querySelector('[data-pb-slot].is-current')?.focus();
    } else if (queueOpts.focusSearchOnSelect !== false) {
      searchInput?.focus();
    }
    if (typeof queueOpts.onSelect === 'function') queueOpts.onSelect(index, queue.current());
    return true;
  }

  function installQueue() {
    queueRefs = buildQueueStrip({ ariaLabel: queueOpts.ariaLabel || 'Slots' });

    // Delegated, because `paintQueue` rebuilds the chips on every state change
    // and a per-chip listener would be re-attached N times a pick.
    queueRefs.strip.addEventListener('click', (event) => {
      const btn = event.target.closest?.('[data-pb-slot]');
      if (!btn || !queueRefs.strip.contains(btn)) return;
      selectSlot(Number(btn.dataset.pbSlot));
    });

    // D11 — THE GAP THE DONOR LEFT, paid at the moment of promotion.
    //
    // The strip shipped as `role="tablist"` with `role="tab"` children and had
    // no arrow handler and no roving tabindex: `session.js` had exactly two
    // addEventListener calls, and the picker's own suite asserted only that the
    // role existed. A tablist a keyboard user cannot move within is a tablist
    // in name. This is the MK-7 lesson verbatim — an accessibility affordance a
    // shared component creates for the first consumer's reason only — and the
    // promotion is the moment to pay it.
    //
    // Automatic activation (arrow moves selection, not just focus), which is
    // what a tablist with `aria-selected` promises and what the click path
    // already does.
    queueRefs.strip.addEventListener('keydown', (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      const n = queue.size;
      if (n < 2) return;
      let next = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        next = (queue.cursor + 1) % n;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        next = (queue.cursor - 1 + n) % n;
      } else if (event.key === 'Home') {
        next = 0;
      } else if (event.key === 'End') {
        next = n - 1;
      }
      if (next === null) return;
      event.preventDefault();
      selectSlot(next, { fromKeyboard: true });
    });

    // ── the header takeover ────────────────────────────────────────────────
    //
    // What you are filling, what you are searching, and out — on one row. The
    // kit's header already IS that row's geometry (flex, centred, a bottom
    // border, the close button at the end), so the queue is inserted into it
    // and the SEARCH is moved into it, rather than a second bar being built
    // underneath a header that only repeats the dialog's name.
    //
    // The `<h2>` survives as the dialog's accessible name — `createModal`
    // pointed `aria-labelledby` at it — and goes visually hidden, because the
    // bar now says something far more specific than the dialog's title: which
    // slot, how many, how far through.
    //
    // `bar: false` opts out for a surface that wants the strip without the
    // takeover. It is not the default because both of this initiative's
    // consumers want it, and an option nothing exercises is a claim.
    if (queueOpts.bar !== false && api.modal) {
      const header = api.modal.querySelector('.pb-modal-header');
      const heading = header?.querySelector('.pb-modal-heading, h2') || null;
      const closeBtn = header?.querySelector('.pb-modal-close') || null;
      const searchWrap = searchInput?.closest('.pb-coll-search-wrap') || null;
      if (header) {
        header.classList.add('pb-coll-bar');
        heading?.classList.add('pb-sr-only');
        header.insertBefore(queueRefs.el, header.firstChild);
        // Before the close button, never appended: the close is the last thing
        // in the row and the last thing in the tab order.
        if (searchWrap) header.insertBefore(searchWrap, closeBtn);
      }
    }
    paintQueue();
  }

  // ── chips ────────────────────────────────────────────────────────────────
  //
  // ONE ROW, measured (CB-14).
  //
  // `pinnedCount` is a COUNT, and a count cannot promise a row: six chips
  // reading "Food Beverage", "Real Estate" and "Photography" wrap, six reading
  // "Legal", "Pets" and "Solar" leave half the row empty. Every surface was
  // guessing a number for its own longest labels — the per-surface-constant
  // problem, in miniature.
  //
  // So the count is a CEILING and the row is measured: paint, read which chips
  // actually landed on the first line, and if any spilled, repaint pinned to
  // what fits. The spill goes to the drawer, which is where the overflow
  // already went — no chip is lost, the "More (N)" button just gets bigger.
  //
  // `_chipFitDepth` is the loop guard. Measuring changes the model, which
  // triggers a repaint, which would measure again forever. It is a bounded
  // DEPTH rather than a "second pass only" boolean because one shrink is not
  // always enough: dropping a chip can leave the row still two lines tall when
  // what spilled was the "More" button, and the pass that fixes that has to be
  // allowed to measure the result.
  const MAX_CHIP_FIT_PASSES = 4;
  let _chipFitDepth = 0;
  let _chipFitWidth = 0;
  /** Chips that fit on line one at the current width; null until measured. */
  let chipFit = null;

  /**
   * What sits on the row's FIRST line: how many chips, and whether anything
   * else spilled off it.
   *
   * The `spilled` half is the whole reason this returns an object. The row is
   * not only chips — "More (N)" and the surface's `trailing` control share it,
   * and NEITHER carries `data-cb-chip`. Counting chips alone therefore reports
   * a perfect fit for a row whose chips fill line one exactly and whose More
   * button was pushed onto line two, which is a two-row chip row that measured
   * itself as a one-row chip row. Measured on Add Section's Hero pane: eight
   * industry chips fit, "More 9" did not.
   *
   * `offsetTop` and not `getBoundingClientRect`: the row may be inside a
   * scrolled container and a transformed, animating modal card, and offsetTop
   * is relative to the offset parent rather than the viewport, so neither moves
   * it. Returns null when nothing can be measured (jsdom, a display:none
   * ancestor), and the caller then leaves the model alone.
   */
  function measureChipRow() {
    const chips = [...chipRow.querySelectorAll('[data-cb-chip]')];
    if (chips.length === 0) return null;
    // Everything at zero means no layout ran — do not "fit" to a phantom.
    if (!chipRow.offsetWidth) return null;
    const top = chips[0].offsetTop;
    let fits = 0;
    for (const chip of chips) {
      if (chip.offsetTop > top) break;
      fits += 1;
    }
    const tail = [
      chipRow.querySelector('[data-cb-drawer-toggle]'),
      chipRow.querySelector('.pb-coll-chips-trailing')
    ];
    return { fits, spilled: tail.some((el) => el && el.offsetTop > top) };
  }

  function paintChips() {
    if (chipOpts === false || !chipRow) return;
    const ceiling = chipOpts.pinnedCount ?? 5;
    const model = buildChipModel(records, {
      keyOf: chipOpts.keyOf,
      // Multi-valued chips (CB-12b): a record that belongs to several keys.
      keysOf: chipOpts.keysOf,
      labelOf: chipOpts.labelOf,
      pinnedCount: chipFit == null ? ceiling : Math.min(ceiling, chipFit),
      activeKey: chipKey
    });
    renderChipRow(chipRow, model, {
      labelOf: chipOpts.labelOf,
      countOf: chipOpts.countOf,
      activeKey: chipKey,
      extra: [
        'all',
        ...(typeof chipOpts.extra === 'function'
          ? chipOpts.extra(records.slice()) || []
          : chipOpts.extra || [])
      ],
      drawerEl,
      drawerOpen,
      // Built once and re-parked on every repaint (see renderChipRow).
      leading: typeof chipOpts.leading === 'function' ? chipOpts.leading() : null,
      trailing: typeof chipOpts.trailing === 'function' ? chipOpts.trailing() : null
    });

    // The measure pass. Skipped on the repaint it causes, and skipped entirely
    // when the surface pins nothing (`pinnedCount: 0` — the SVG library puts
    // every chip in the drawer on purpose) or has no overflow to move anything
    // into.
    if (_chipFitDepth >= MAX_CHIP_FIT_PASSES || ceiling === 0) return;
    const measured = measureChipRow();
    if (measured == null) return;
    _chipFitWidth = chipRow.offsetWidth;
    // `fits` counts the rendered chips, which include the `extra` keys ('all',
    // 'favorites', …) that are not part of the pinned budget. The budget is
    // what is left after them.
    const extras = chipRow.querySelectorAll('[data-cb-chip]').length - model.pinned.length;
    let budget = Math.max(0, measured.fits - extras);
    if (budget >= model.pinned.length) {
      // Every chip fits. If nothing else spilled the row is done; if More or
      // the trailing control landed on line two, give up one chip to it. There
      // is nothing else to surrender — the extras and the leading control are
      // not the pinned budget's to spend.
      if (!measured.spilled || model.pinned.length === 0) return;
      budget = model.pinned.length - 1;
    }
    if (budget === chipFit) return; // shrinking further would change nothing
    chipFit = budget;
    _chipFitDepth += 1;
    try {
      paintChips();
    } finally {
      _chipFitDepth -= 1;
    }
  }

  /**
   * A width change invalidates the measurement — a wider dialog fits more chips
   * and a narrower one fewer, and a stale `chipFit` would strand chips in the
   * drawer that now have room. Re-measured from scratch rather than adjusted.
   */
  function remeasureChips() {
    if (chipOpts === false || !chipRow || closed) return;
    if (chipRow.offsetWidth === _chipFitWidth) return;
    chipFit = null;
    paintChips();
  }

  function setChip(key) {
    if (closed) return;
    drawerOpen = false;
    if (key === chipKey) {
      paintChips();
      return;
    }
    chipKey = key;
    paintChips();
    // Chip changes filter IN PLACE too — a strict improvement on the donor,
    // which rebuilds.
    applyFilter();
    if (typeof chipOpts.onChange === 'function') chipOpts.onChange(chipKey);
  }

  // ── selection UI ─────────────────────────────────────────────────────────
  // The select-all control (selection.selectAll, default on): a kit checkbox
  // in the toolbar whose tri-state derives from selectAllState() — checked,
  // indeterminate on a partial visible selection, disabled when nothing in
  // view is selectable. Filter-scoped by construction: it drives
  // selectAllVisible, never the whole record set.
  let selectAllBox = null;
  if (selection && selOpts.selectAll !== false) {
    const toolbar = chrome.shell.querySelector('.pb-coll-toolbar');
    if (toolbar) {
      selectAllBox = createCheckbox({
        ariaLabel: 'Select all shown',
        className: 'pb-coll-selectall',
        onChange: (event) => selection.selectAllVisible(event.target.checked)
      });
      toolbar.appendChild(selectAllBox.wrap);
    }
  }

  function syncSelectionUI() {
    if (!selection) return;
    const selected = selection.store.getSet();
    for (const [id, entry] of _entries) {
      entry.el.classList.toggle('is-selected', selected.has(id));
      const box = entry.el.querySelector(`input[data-cb-action="${ACTION_TOGGLE}"]`);
      if (box) box.checked = selected.has(id);
    }
    if (selectAllBox) {
      const state = selection.selectAllState();
      selectAllBox.setChecked(state.checked, { indeterminate: state.indeterminate });
      selectAllBox.input.disabled = state.disabled;
    }
    const { count, hiddenCount } = selection.summarize();
    if (selbar) {
      selbar.hidden = count === 0;
      const countEl = selbar.querySelector('.pb-coll-selbar-count');
      if (countEl) {
        const noun = selOpts.noun || 'item';
        const plural = selOpts.nounPlural || `${noun}s`;
        countEl.textContent =
          `${count} ${count === 1 ? noun : plural} selected` +
          (hiddenCount > 0 ? ` · ${hiddenCount} hidden by filter` : '');
      }
    }
    if (liveRegion) liveRegion.textContent = selection.announcement();
    if (typeof selOpts.onChange === 'function')
      selOpts.onChange({ ids: selection.getSelectedIds(), count, hiddenCount });
  }

  function buildSelbarActions() {
    if (!selection || !selbar) return;
    for (const intent of selOpts.bulkIntents || []) {
      const btn = createButton({
        variant: intent.variant === 'danger' ? 'danger' : 'outline',
        size: 'sm',
        label: intent.label,
        dataset: { cbBulk: intent.id }
      });
      btn.addEventListener('click', () => runBulkIntent(intent, btn));
      selbar.appendChild(btn);
    }
    selbar.appendChild(
      createButton({
        variant: 'ghost',
        size: 'sm',
        label: 'Clear',
        dataset: { cbBulk: '__clear' },
        onClick: () => selection.clear()
      })
    );
  }

  async function runBulkIntent(intent, btn) {
    // Browser-level in-flight flag AND per-button busy: the same operation is
    // reachable from a row icon and the bulk bar, and a per-button guard alone
    // was not enough (project-history-modal.js:526 lesson).
    if (actionInFlight || closed) return;
    if (typeof intent.enabled === 'function' && !intent.enabled(selectedItems())) return;
    actionInFlight = true;
    setButtonBusy(btn, true, { busyLabel: intent.busyLabel ?? null });
    try {
      await intent.perform(selectedItems(), api);
    } finally {
      actionInFlight = false;
      if (btn.isConnected) setButtonBusy(btn, false);
    }
  }

  function selectedItems() {
    return selection
      .getSelectedIds()
      .map((id) => _entries.get(id)?.item)
      .filter(Boolean);
  }

  // ── rendering (the three rebuild triggers only) ──────────────────────────
  function renderList() {
    if (closed) return;
    if (settleCancel) settleCancel();
    // THE RENAME CONTRACT (CB-7): a row marked `state.editing` is never
    // re-rendered — its live element (and the input the user is typing into)
    // is carried across the rebuild wholesale, focus included. A module that
    // rebuilds mid-rename discards typed text with no error.
    const editing = new Map();
    for (const [id, entry] of _entries) {
      if (entry.item.state?.editing) {
        editing.set(id, {
          entry,
          hadFocus: entry.el.contains(document.activeElement),
          focused: entry.el.contains(document.activeElement) ? document.activeElement : null
        });
      }
    }
    listEl.textContent = '';
    listEl.removeAttribute('aria-busy');
    _entries.clear();
    delete listEl.dataset.settled;

    const frag = document.createDocumentFragment();
    records.forEach((record, i) => {
      const item = opts.adapt(record, i);
      const id = String(item.id ?? idOf(record, i));
      item.id = id;
      const kept = editing.get(id);
      if (kept) {
        _entries.set(id, kept.entry);
        frag.appendChild(kept.entry.el);
        return;
      }
      const refs = renderCollectionItem(item, {
        layout,
        itemTag: opts.itemTag || (opts.listTag === 'div' ? 'div' : 'li'),
        selectable: !!selection,
        itemRole: opts.itemRole,
        selectControl: isSingle ? 'radio' : 'checkbox',
        radioName: isSingle ? radioGroupName : undefined,
        query
      });
      _entries.set(id, { item, record, ...refs });
      frag.appendChild(refs.el);
      if (typeof opts.onItemIntent === 'function') {
        let fired = false;
        const prefetch = () => {
          if (fired || closed) return;
          fired = true;
          opts.onItemIntent(item, { api });
        };
        refs.el.addEventListener('pointerenter', prefetch, { once: true });
        refs.el.addEventListener('focus', prefetch, { once: true });
      }
      if (thumbPool && refs.posterImg && !refs.posterImg.src) {
        thumbPool.enqueue(item, refs.posterImg);
      }
    });
    // Fragment-then-single-append: one layout pass, not N.
    listEl.appendChild(frag);
    parkTiles();
    for (const kept of editing.values()) {
      if (kept.hadFocus && kept.focused?.isConnected) {
        kept.focused.focus({ preventScroll: true });
      }
    }
    // Tiles are exempt from the stagger: a leading tile would delay every
    // record one step and a trailing one would trail the whole grid.
    applyStagger(listEl.querySelectorAll('.pb-coll-item:not(.pb-coll-tile)'));
    settleCancel = armSettle(listEl);
    if (selection) selection.prune();
    applyFilter();
    // MK-7: re-arm the roving tab stop. `gridKeyNav` syncs once at setup, and
    // setup happens while this list is still EMPTY because `load()` is async —
    // so without this no grid surface had a tab stop and a keyboard-only user
    // could not reach the results at all. `applyFilter` runs first: the sync
    // resolves `:not([hidden])`, so it has to see the final visibility.
    gridNav?.sync?.();
    // Post-render hook: the one place a surface re-attaches its FOREIGN
    // siblings (group labels, disclosure panels) after a rebuild — the module
    // tolerates them because its filter iterates _entries, never children.
    if (typeof opts.onRender === 'function') opts.onRender(api);
  }

  // ── loading ──────────────────────────────────────────────────────────────
  function paintSkeleton() {
    listEl.setAttribute('aria-busy', 'true');
    const skel = opts.skeleton || {};
    const count = typeof skel.count === 'function' ? skel.count() : (skel.count ?? 6);
    listEl.innerHTML = isRows
      ? renderSkeletonRows(count, {
          variant: skel.rowVariant || 'revision',
          itemTag: opts.itemTag || 'li'
        })
      : renderSkeletonGrid(count, {
          variant: skel.variant || 'project',
          itemTag: opts.itemTag || 'li'
        });
  }

  async function runLoad({ initial = false } = {}) {
    const run = beginRun();
    const my = run.runId;
    const { signal } = run;
    loadError = null;

    // Initial load paints the skeleton IMMEDIATELY — no createDelayedLoader:
    // it would fire in the same tick the modal opens, and a 140ms anti-flash
    // delay would leave a blank dialog. The modal appearing IS the
    // acknowledgement. Refresh delays, guarded on an already-populated list.
    let delayed = null;
    if (initial) {
      paintSkeleton();
    } else {
      delayed = createDelayedLoader({
        show: () => {
          if (listEl.childElementCount === 0) paintSkeleton();
        },
        hide: () => {}
      });
      delayed.start();
    }
    try {
      const result = await opts.load({ signal, runId: my });
      if (closed || my !== runId) return;
      records = Array.isArray(result) ? result : [];
      paintChips();
      renderList();
    } catch (err) {
      if (closed || my !== runId) return;
      if (err?.name === 'AbortError') return;
      loadError = err;
      listEl.textContent = '';
      listEl.removeAttribute('aria-busy');
      _entries.clear();
      // A failed load still offers "New project": the tiles are parked back
      // and decide whether the list stays visible. Deliberately NOT the full
      // applyFilter — that would fire onFilter on the error path, which no
      // caller ever saw before tiles existed.
      parkTiles();
      paintState(0, { keepList: applyTileFilter() > 0 });
      paintDisclosure(0);
    } finally {
      delayed?.stop();
      if (initial && !readySettled) {
        readySettled = true;
        resolveReady?.(api);
      }
    }
  }

  // ── delegated listeners (exactly three, on listEl) ───────────────────────
  function itemIdFor(target) {
    const el = target.closest('[data-cb-id]');
    return el && listEl.contains(el) ? el.dataset.cbId : null;
  }

  function checkboxIntentFromPointer(event) {
    if (event?.shiftKey && (event.metaKey || event.ctrlKey)) return 'range-add';
    if (event?.shiftKey) return 'range';
    return 'toggle';
  }

  listEl.addEventListener('click', async (event) => {
    // Tiles first (PP-0): dismiss before activate, because the dismiss button
    // sits INSIDE the tile container and must never fall through to it.
    const dismissEl = event.target.closest('[data-cb-tile-dismiss]');
    if (dismissEl && listEl.contains(dismissEl)) {
      event.preventDefault();
      event.stopPropagation();
      const tile = _tiles.get(dismissEl.dataset.cbTileDismiss);
      if (!tile || typeof tile.spec.onDismiss !== 'function') return;
      const hadFocus = tile.el.contains(document.activeElement);
      const priorIndex = hadFocus ? visibleNavigableEls().indexOf(tile.el) : -1;
      await runTileAction(tile, tile.spec.onDismiss);
      // The surface usually removes the tile inside onDismiss (setTiles), which
      // strands focus on <body>; a tile that merely hid needs the same rescue.
      const lostFocus =
        !tile.el.isConnected || tile.el.hidden || !tile.el.contains(document.activeElement);
      if (hadFocus && !closed && lostFocus) focusAfterDismiss(Math.max(0, priorIndex));
      return;
    }
    const activateEl = event.target.closest('[data-cb-tile-activate]');
    if (activateEl && listEl.contains(activateEl)) {
      event.preventDefault();
      const tile = _tiles.get(activateEl.dataset.cbTileActivate);
      if (tile) runTileAction(tile, tile.spec.onActivate);
      return;
    }
    const actionEl = event.target.closest('[data-cb-action]');
    if (!actionEl || !listEl.contains(actionEl)) {
      // CB-10: the whole ROW is the click target on a picker. The donor made
      // that work by wrapping the row in a <label>, which this module cannot
      // reproduce — its control is already inside one, and nested labels are
      // invalid — so the row's own click stands in for the label.
      //
      // It therefore behaves exactly like the CONTROL it replaces: a plain
      // click TOGGLES (Shift still ranges), it does not replace. Finder
      // semantics — `resolveClickIntent`, where a plain click replaces the
      // selection — are right for a file grid and wrong for a checkbox list:
      // ticking a second page would silently untick the first. Off by default;
      // on every other surface a row click means "open this", not "select it".
      const rowId = itemIdFor(event.target);
      if (!rowId) return;
      // A click that lands on the control itself is the control's — routing it
      // here too would apply the intent twice and cancel the toggle out.
      if (event.target.closest(`input[data-cb-action="${ACTION_TOGGLE}"]`)) return;
      if (selection && selOpts.rowClick) {
        event.preventDefault();
        selection.applyIntent(rowId, isSingle ? 'replace' : checkboxIntentFromPointer(event));
        return;
      }
      // `activateOnItemClick` (CB-11): the ITEM is the button. A listbox option
      // is picked by clicking it, and a card with no poster-button and no row
      // actions would otherwise be inert to the mouse while still being
      // operable by Enter through gridKeyNav — a keyboard-only affordance
      // nobody would notice was missing until they clicked.
      if (opts.activateOnItemClick && typeof opts.onActivate === 'function') {
        const entry = _entries.get(rowId);
        if (entry) {
          event.preventDefault();
          opts.onActivate(entry.item, { api, event });
        }
      }
      return;
    }
    const id = itemIdFor(actionEl);
    if (!id) return;
    const action = actionEl.dataset.cbAction;
    if (action === ACTION_TOGGLE) {
      // A real checkbox `change` is a plain Event in Chromium, so Shift/Cmd
      // modifiers are only observable on the preceding pointer click.
      if (selection && actionEl.tagName === 'INPUT') {
        actionEl.dataset.cbIntent = checkboxIntentFromPointer(event);
      }
      return; // change handles the actual store write
    }
    event.preventDefault();
    const entry = _entries.get(id);
    if (!entry) return;
    if (action === ACTION_ACTIVATE) {
      if (typeof opts.onActivate === 'function') opts.onActivate(entry.item, { api, event });
      return;
    }
    if (typeof opts.onAction !== 'function') return;
    if (actionInFlight) return;
    const result = opts.onAction(action, entry.item, { api, event, button: actionEl });
    if (result && typeof result.then === 'function') {
      actionInFlight = true;
      setButtonBusy(actionEl, true, { busyLabel: null });
      try {
        await result;
      } finally {
        actionInFlight = false;
        if (actionEl.isConnected) setButtonBusy(actionEl, false);
      }
    }
  });

  listEl.addEventListener('change', (event) => {
    const input = event.target.closest(`input[data-cb-action="${ACTION_TOGGLE}"]`);
    if (!input || !selection) return;
    const id = itemIdFor(input);
    if (!id) return;
    // A radio cannot be un-picked, and picking one must UNPICK the other — that
    // is `replace`, not `toggle`, and it is the mode's semantics rather than
    // the gesture's, so no modifier can override it.
    if (isSingle) {
      delete input.dataset.cbIntent;
      selection.applyIntent(id, 'replace');
      return;
    }
    const intent =
      input.dataset.cbIntent ||
      (event.shiftKey || input.dataset.cbShift === 'true' ? 'range' : 'toggle');
    delete input.dataset.cbIntent;
    delete input.dataset.cbShift;
    selection.applyIntent(id, intent);
  });

  listEl.addEventListener('keydown', (event) => {
    if (!selection) return;
    // A tile is not selectable; no selection callback may fire for it.
    if (event.target.closest?.('[data-cb-tile]')) return;
    const id = itemIdFor(event.target);
    const intent = selection.handleRowKey(event, id);
    if (!intent) return;
    if (intent.type === 'focus-move' && isRows) {
      event.preventDefault();
      const target = nextFocusTarget(id, visibleIds(), intent.dir);
      const next = target && _entries.get(target);
      if (next) next.el.focus({ preventScroll: false });
    } else if (intent.type !== 'focus-move') {
      event.preventDefault();
    }
  });

  // Roving grid focus. The selector resolves the visible set at PRESS time.
  /** @type {(() => void) & {sync?: () => void}} */
  let gridNav = null;
  if (!isRows) {
    cleanups.push(
      (gridNav = gridKeyNav(listEl, '.pb-coll-item:not([hidden])', {
        wrap: false,
        escapeUpTo: searchInput || undefined,
        // A tile's focus target is its primary <button>; Enter/Space there are
        // the button's own click, and the dismiss button owns its own keys.
        focusTargetOf: (el) => el.querySelector('[data-cb-tile-activate]') || el,
        ignoreWithin: '[data-cb-tile-dismiss]',
        onSelect: (el) => {
          if (el.dataset.cbTile !== undefined) return;
          const entry = _entries.get(el.dataset.cbId);
          if (entry && typeof opts.onActivate === 'function') opts.onActivate(entry.item, { api });
        }
      }))
    );
  }

  /** The first visible item Enter-from-search may activate: a record, or a
   *  tile only when the query is non-empty and the tile claims the match. */
  function firstActivatable() {
    for (const el of listEl.querySelectorAll('.pb-coll-item:not([hidden])')) {
      if (el.dataset.cbTile !== undefined) {
        const tile = _tiles.get(el.dataset.cbTile);
        if (
          tile &&
          query &&
          typeof tile.spec.matchesQuery === 'function' &&
          tile.spec.matchesQuery(query)
        ) {
          return { tile };
        }
        continue;
      }
      const entry = _entries.get(el.dataset.cbId);
      if (entry) return { entry };
    }
    return null;
  }

  // ── search wiring ────────────────────────────────────────────────────────
  if (searchInput) {
    searchDebounce = debounce(() => {
      if (closed) return;
      applyFilter();
      // `onQuery` (CB-11): a tab backed by a REMOTE index searches rather than
      // filters. It runs after the local pass, which is a no-op for records
      // that were themselves fetched for this query, and it is the surface's
      // job to feed the result back through setRecords().
      if (typeof searchOpts.onQuery === 'function') searchOpts.onQuery(query);
    }, searchOpts.debounceMs ?? 130);
    searchInput.value = query;
    searchInput.addEventListener('input', () => {
      query = searchInput.value.trim().toLowerCase();
      searchDebounce();
    });
    searchInput.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && searchOpts.clearOnEscape !== false && searchInput.value) {
        // Clears and does NOT close — stop the trap from seeing this Escape.
        event.preventDefault();
        event.stopPropagation();
        searchInput.value = '';
        query = '';
        applyFilter();
      } else if (event.key === 'ArrowDown' && searchOpts.arrowDownEntersList !== false) {
        // NAVIGATION includes tiles: the first visible item in DOM order, which
        // for a leading tile is its primary button.
        const firstEl = listEl.querySelector('.pb-coll-item:not([hidden])');
        if (firstEl) {
          event.preventDefault();
          (firstEl.querySelector('[data-cb-tile-activate]') || firstEl).focus();
        }
      } else if (event.key === 'Enter' && searchOpts.enterActivatesFirstVisible !== false) {
        // The first VISIBLE item — `filtered[0]` could open one the query
        // excluded (the donor's fixed bug, kept fixed here). ACTIVATION skips
        // tiles unless the query names one: Enter means "open what I searched
        // for", and a "New project" tile at the empty query must not hijack it.
        const target = firstActivatable();
        if (!target) return;
        if (target.tile) {
          event.preventDefault();
          runTileAction(target.tile, target.tile.spec.onActivate);
        } else if (typeof opts.onActivate === 'function') {
          event.preventDefault();
          opts.onActivate(target.entry.item, { api });
        }
      }
    });
  }

  if (chipRow) {
    chrome.shell.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-cb-drawer-toggle]');
      if (toggle && chrome.shell.contains(toggle)) {
        drawerOpen = !drawerOpen;
        paintChips();
        return;
      }
      const chip = event.target.closest('[data-cb-chip]');
      if (chip) setChip(chip.dataset.cbChip);
    });
  }

  // The chip row's fit is a function of its WIDTH, so the width has to be
  // watched. Guarded on the constructor: jsdom has no ResizeObserver, and the
  // fit pass is a no-op there anyway (nothing lays out), so the suites see
  // exactly the model `pinnedCount` asked for.
  if (chipOpts !== false && chipRow && typeof ResizeObserver === 'function') {
    const ro = new ResizeObserver(() => remeasureChips());
    ro.observe(chipRow);
    cleanups.push(() => ro.disconnect());
  }

  if (selection) {
    cleanups.push(selection.subscribe(() => syncSelectionUI()));
    buildSelbarActions();
  }

  // Toolbar whole-set predicates — deliberately NOT bulkIntents and NOT
  // selectAllVisible: they compute a set from the FULL record list.
  if (Array.isArray(opts.toolbarActions) && opts.toolbarActions.length && selection) {
    const bar = document.createElement('div');
    bar.className = 'pb-coll-toolbar-actions';
    if (opts.toolbarActionsClass) bar.classList.add(opts.toolbarActionsClass);
    for (const action of opts.toolbarActions) {
      bar.appendChild(
        createButton({
          variant: 'outline',
          size: 'sm',
          label: action.label,
          dataset: { cbToolbar: action.id },
          onClick: () => selection.setSelection(action.compute(records.slice()))
        })
      );
    }
    chrome.shell.insertBefore(bar, scroller);
  }

  // ── detail view ──────────────────────────────────────────────────────────
  //
  // THE DETAIL STAGE'S CHROME IS THE ENGINE'S (2026-09-08).
  //
  // Back, and the one control that commits what the detail is showing, ride the
  // DIALOG HEADER. They are navigation out of this view and the action it
  // exists for — chrome, which belongs in the frame rather than on a strip
  // inside the scrolling body.
  //
  // It lives here because three surfaces wanted it and the first one to get it
  // wrote the condition down: if a second one wants it, the pattern moves into
  // the engine rather than being copied. The three had three different answers
  // — a top strip (template selector), a bottom row after the content (image
  // picker, where the primary action could sit below the fold), and a header
  // row (the variant picker, hand-rolled).
  //
  // The engine owns Back and the placement; the surface supplies only its
  // commit control, because that half is genuinely per-surface: the template
  // selector's compose button rewrites its own label asynchronously and the
  // image picker's Use is disabled until a variant is chosen.
  let _detailChrome = null;

  function clearDetailChrome() {
    if (!_detailChrome) return;
    const { nodes, hidden, headingEl, headingWasSrOnly } = _detailChrome;
    _detailChrome = null;
    for (const node of nodes) node.remove();
    // Only un-hide a heading THIS mount hid. A surface can keep its title
    // permanently off screen (the variant picker does, via `headingSrOnly`),
    // and restoring unconditionally would put it back the first time someone
    // opened a detail and came out of it.
    if (headingEl && !headingWasSrOnly) headingEl.classList.remove('pb-sr-only');
    // Whatever was already occupying the trailing slot comes back. The variant
    // picker parks its CodeStitch/All-sources toggle there, and while a detail
    // is open there is no list on screen for it to narrow.
    for (const [el, was] of hidden) el.hidden = was;
  }

  function mountDetailChrome(item) {
    clearDetailChrome();
    const cfg = opts.detail || {};
    if (cfg.chrome === false) return;

    const nodes = [];
    const backBtn = createButton({
      variant: 'ghost',
      size: 'sm',
      pill: true,
      className: 'pb-coll-detail-back pb-dialog-header-action',
      label: typeof cfg.back === 'string' ? cfg.back : 'Back',
      testid: 'collection-detail-back',
      // `onBack` OWNS the return when a surface supplies one, rather than
      // running beside the default: the template selector restores a queued
      // catalog and re-focuses the card it came from, and both have to happen
      // around `showList()`, not before it.
      onClick: () => {
        if (typeof cfg.onBack === 'function') cfg.onBack(api);
        else showList();
      }
    });
    nodes.push(backBtn);

    const supplied = typeof cfg.actions === 'function' ? cfg.actions(item, api) : null;
    const actionEls = (Array.isArray(supplied) ? supplied : supplied ? [supplied] : []).filter(
      Boolean
    );
    for (const el of actionEls) {
      el.classList.add('pb-dialog-header-action');
      nodes.push(el);
    }

    const header = api.header || null;
    const hidden = new Map();
    let headingEl = null;
    let headingWasSrOnly = false;
    if (header) {
      const heading =
        header.querySelector('.pb-modal-heading, .pb-coll-popover-heading, h2') || null;
      const closeBtn = header.querySelector('.pb-modal-close') || null;
      for (const el of header.querySelectorAll('.pb-dialog-header-trailing')) {
        hidden.set(el, el.hidden);
        el.hidden = true;
      }
      // The dialog's TITLE names the LIST — "Choose a template", "Hero
      // variants" — and you are no longer looking at the list. The detail body
      // names the thing you ARE looking at, so the title is the same fact twice
      // and the breadcrumb is what belongs in the frame. Hidden rather than
      // removed: `aria-labelledby` points at it, so dropping the node would
      // leave the dialog with no accessible name.
      if (heading) {
        headingWasSrOnly = heading.classList.contains('pb-sr-only');
        headingEl = heading;
        heading.classList.add('pb-sr-only');
      }
      header.insertBefore(backBtn, heading || header.firstChild);
      // The first action takes the trailing slot, so the placement rule is the
      // kit's one rather than a second copy of it.
      actionEls[0]?.classList.add('pb-dialog-header-trailing');
      for (const el of actionEls) header.insertBefore(el, closeBtn);
      header.classList.add('pb-coll-header-detail');
      nodes.push({ remove: () => header.classList.remove('pb-coll-header-detail') });
    } else {
      // No header — `mountCollectionList` used bare (the render harness). The
      // controls still have to exist and still have to work: a detail stage
      // with no way out is not an acceptable degradation.
      const bar = document.createElement('div');
      bar.className = 'pb-coll-detail-bar';
      bar.append(backBtn, ...actionEls);
      stage.detailView.appendChild(bar);
      nodes.length = 0;
      nodes.push(bar);
    }

    _detailChrome = { nodes, hidden, backBtn, actionEls, headingEl, headingWasSrOnly };
    return backBtn;
  }

  /**
   * Supply (or replace) the detail stage's commit controls after mount.
   *
   * `detail.actions` is the declarative form and runs before `detail.render`.
   * A surface that BUILDS its action inside an async render — the template
   * selector creates its Use/compose pair only once the page map is in hand —
   * has nothing to hand over at that point, so it calls this instead. Same
   * slot, same classes, same teardown; no second placement rule.
   */
  function setDetailActions(elements) {
    if (!_detailChrome) return;
    const next = (Array.isArray(elements) ? elements : elements ? [elements] : []).filter(Boolean);
    for (const el of _detailChrome.actionEls) el.remove();
    _detailChrome.nodes = _detailChrome.nodes.filter((n) => !_detailChrome.actionEls.includes(n));
    _detailChrome.actionEls = next;
    for (const el of next) {
      el.classList.add('pb-dialog-header-action');
      _detailChrome.nodes.push(el);
    }
    next[0]?.classList.add('pb-dialog-header-trailing');
    const header = api.header || null;
    if (header) {
      const closeBtn = header.querySelector('.pb-modal-close') || null;
      for (const el of next) header.insertBefore(el, closeBtn);
    } else {
      _detailChrome.backBtn.parentElement?.append(...next);
    }
  }

  function showDetail(id) {
    const entry = _entries.get(String(id));
    if (!entry || !opts.detail || typeof opts.detail.render !== 'function') return;
    const run = beginRun();
    const my = run.runId;
    if (detailTeardown) {
      try {
        detailTeardown();
      } catch {
        /* a broken teardown must not strand the stage */
      }
      detailTeardown = null;
    }
    stage.detailView.textContent = '';
    stage.showDetail({ origin: entry.el, resetScroll: opts.detail.resetScroll !== false });
    const backBtn = mountDetailChrome(entry.item);
    detailTeardown =
      opts.detail.render(entry.item, {
        view: stage.detailView,
        api,
        runId: my,
        signal: run.signal
      }) || null;
    // The way out gets the caret unless the surface's own render moved it —
    // a detail stage that opens with focus on the document is unreachable by
    // keyboard until you tab through the whole card.
    if (backBtn && !stage.detailView.contains(document.activeElement)) {
      backBtn.focus({ preventScroll: true });
    }
  }

  function showList() {
    beginRun({ needsSignal: false });
    clearDetailChrome();
    if (detailTeardown) {
      try {
        detailTeardown();
      } catch {
        /* ditto */
      }
      detailTeardown = null;
    }
    stage.showList({ focus: 'origin' });
  }

  // ── teardown — the order is the contract ─────────────────────────────────
  function teardown() {
    if (closed) return;
    clearDetailChrome();
    closed = true; // 1
    runId += 1; // 2
    openAbort.abort(); // 3
    runAbort?.abort();
    searchDebounce?.cancel?.(); // 4
    if (settleCancel) settleCancel(); // 5
    if (pendingNewTimer) {
      clearTimeout(pendingNewTimer);
      pendingNewTimer = null;
    }
    if (detailTeardown) {
      try {
        detailTeardown(); // 6
      } catch {
        /* teardown must complete */
      }
      detailTeardown = null;
    }
    thumbPool?.cancel(); // 7
    for (const io of observers) io.disconnect(); // 8
    observers.clear();
    // 9 — image skeleton/fallback removers are caller-owned via cleanups
    for (const fn of cleanups.splice(0)) {
      try {
        fn(); // 10/11 — bus unsubs + selection unsub
      } catch {
        /* one bad cleanup must not stop the rest */
      }
    }
    if (selection) selection.store.clear({ source: 'collection-close' });
    if (typeof opts.thumbnails?.releaseAll === 'function') {
      // 12 — LAST among resource work: a worker mid-await could still be
      // about to write img.src from a URL about to be revoked; steps 1, 3
      // and 7 have already fenced that.
      opts.thumbnails.releaseAll();
    }
    if (typeof opts.onClose === 'function') opts.onClose(); // 13
    if (!readySettled) {
      readySettled = true;
      resolveReady?.(api);
    }
  }

  function close() {
    if (closed) return;
    // A REQUEST, not the teardown: with a host (the modal) it goes through the
    // dialog's own close so backdrop, Escape, × and programmatic all land on
    // one path, and the dialog calls destroy() back. Without one, there is no
    // animation to wait for.
    if (typeof opts.onRequestClose === 'function') opts.onRequestClose();
    else teardown();
  }

  // ── the handle ───────────────────────────────────────────────────────────
  const api = {
    close,
    ready,
    /** The ordered teardown. The dialog's onClose calls this; embedded callers
     *  own it themselves. Idempotent. */
    destroy: teardown,
    isClosed: () => closed,
    listEl,
    shell: chrome.shell,
    scroller,
    searchInput,
    metaEl,
    chipRow,
    drawerEl,
    /** The right-hand toolbar slot's wrapper, or null when none was asked for. */
    trailingEl,
    listView: stage.listView,
    detailView: stage.detailView,
    // DS-3: the open-lifetime abort signal. A surface that fetches per-record
    // (the decorative library's inline previews) wires its pool to this so the
    // work dies with the dialog rather than resolving into a detached tree.
    openAbortSignal: openAbort.signal,

    getRecords: () => records.slice(),
    getItems: () => [..._entries.values()].map((entry) => entry.item),
    setRecords(next) {
      if (closed) return;
      beginRun({ needsSignal: false });
      records = Array.isArray(next) ? next.slice() : [];
      paintChips();
      renderList();
    },
    refresh: () => runLoad({ initial: false }),
    removeRecords(ids) {
      if (closed) return;
      const drop = new Set((ids || []).map(String));
      records = records.filter((r, i) => !drop.has(String(idOf(r, i))));
      // Optimistic removal does NOT re-render survivors: drop the elements,
      // prune the selection, repaint counts. Sentinels on surviving <img>s
      // must survive — collection-surface-tests pins it.
      for (const id of drop) {
        const entry = _entries.get(id);
        if (entry) {
          entry.el.remove();
          _entries.delete(id);
        }
      }
      if (selection) selection.prune();
      applyFilter();
    },
    restoreRecords(next) {
      this.setRecords(next);
    },

    itemEl: (id) => _entries.get(String(id))?.el || null,
    getItem: (id) => _entries.get(String(id))?.item || null,
    updateItem(id, patch) {
      const entry = _entries.get(String(id));
      if (!entry) return;
      entry.item = { ...entry.item, ...patch };
      updateCollectionItem(entry, entry.item, { query });
    },
    setItemBusy(id, busy) {
      this.updateItem(id, { state: { ...(_entries.get(String(id))?.item.state || {}), busy } });
    },
    setItemEditing(id, editing) {
      this.updateItem(id, {
        state: { ...(_entries.get(String(id))?.item.state || {}), editing }
      });
    },

    /** Re-run the visibility pass (fold state changed, no data change). */
    refilter() {
      applyFilter();
    },
    setQuery(value) {
      query = String(value ?? '')
        .trim()
        .toLowerCase();
      if (searchInput) searchInput.value = String(value ?? '');
      applyFilter();
    },
    getQuery: () => query,
    setChip,
    repaintChips: paintChips,
    getChip: () => chipKey,
    getVisibleIds: visibleIds,

    getSelectedIds: () => (selection ? selection.getSelectedIds() : []),
    getSelectedItems: () => (selection ? selectedItems() : []),
    setSelection: (ids) => selection?.setSelection(ids),
    clearSelection: () => selection?.clear(),
    selectAllVisible: (checked = true) => selection?.selectAllVisible(checked),

    showDetail,
    showList,
    setDetailActions,
    activeView: () => stage.activeView(),

    setStatus(text) {
      if (liveRegion) liveRegion.textContent = String(text ?? '');
      else if (metaEl) metaEl.textContent = String(text ?? '');
    },
    focusSearch() {
      searchInput?.focus();
    },

    /** PP-0: the disclosure row element (null when the option is absent). */
    discloseEl,
    /** PP-0: a tile's container element by id, or null. */
    tileEl: (id) => _tiles.get(String(id))?.el || null,
    /** PP-0: replace the tiles live (a clipboard change). Validates like the
     *  option does; a same-id tile that held focus keeps it. */
    setTiles(next) {
      if (closed) return;
      const focusedTile = [..._tiles.values()].find((t) => t.el.contains(document.activeElement));
      const focusedId = focusedTile?.spec.id;
      const focusedPart =
        focusedTile?.dismissBtn === document.activeElement
          ? 'dismiss'
          : focusedTile
            ? 'primary'
            : null;
      tileSpecs = normalizeTiles(next);
      for (const tile of _tiles.values()) tile.el.remove();
      buildTiles();
      parkTiles();
      applyFilter();
      const again = focusedId && _tiles.get(focusedId);
      if (again && !again.el.hidden) {
        const target =
          focusedPart === 'dismiss' && again.dismissBtn ? again.dismissBtn : again.primaryBtn;
        target.focus({ preventScroll: true });
      }
    },

    // DS-1: the slot queue. `null` on a surface that passed no `queue`, which
    // is what lets a consumer read it without branching on the option.
    queue,
    /** Repaint the strip — after a consumer has marked a slot filled. */
    paintQueue,
    /** Move the cursor as if the chip were clicked. */
    selectSlot: (index) => selectSlot(index),

    /** Register teardown work (bus unsubs, observers) the close path runs. */
    addCleanup(fn) {
      if (typeof fn === 'function') cleanups.push(fn);
    },
    addObserver(io) {
      observers.add(io);
    }
  };

  // Merged BEFORE onOpen: surfaces build their foreign chrome against
  // `api.body`, so the handle has to be whole by the time they see it.
  if (opts.apiExtras) Object.assign(api, opts.apiExtras);

  // The queue is installed AFTER apiExtras and BEFORE onOpen, and both halves
  // of that are load-bearing: it takes over `api.modal`'s header, which only
  // exists once `openCollectionBrowser` has merged it in; and a surface's
  // `onOpen` must find `api.queue` already there, because that is where it
  // wires its own pick path to the cursor.
  if (queueOpts) installQueue();

  if (typeof opts.onOpen === 'function') opts.onOpen(api);
  runLoad({ initial: true });
  return api;
}

/**
 * The modal form — the engine above, inside a dialog. Every migrated surface
 * calls this; nothing about its handle, its options or its DOM changed when
 * the engine moved out from under it (CB-12a).
 */
export function openCollectionBrowser(options) {
  const opts = options || {};
  if (typeof opts.load !== 'function') throw new Error('openCollectionBrowser requires load()');
  if (typeof opts.adapt !== 'function') throw new Error('openCollectionBrowser requires adapt()');

  // Testid open guard — `.pb-history-overlay`-style CLASS guards silently
  // no-op when another surface shares the class; the testid is this dialog's.
  // An overlay mid-close (animated removal) does NOT block a reopen: it is
  // already leaving, and treating it as "open" makes a close-then-reopen race
  // a silent no-op.
  const guardSelector =
    opts.guardSelector ?? (opts.testid ? `[data-testid="${opts.testid}"]` : null);
  if (guardSelector) {
    const existing = [...document.querySelectorAll(guardSelector)].filter(
      (el) => !el.closest('.pb-modal-overlay-closing')
    );
    if (existing.length > 0) return null;
  }

  let list = null;

  // PP-0: `initialFocus: 'search'` is the shorthand every collection dialog
  // wants — resolved LAZILY because `list` (and its input) does not exist until
  // the mount below. Anything else is forwarded verbatim; absent stays absent
  // (the kit's synchronous close-button focus), so no surface changes until it
  // opts in.
  const initialFocus =
    opts.initialFocus === 'search' ? () => list?.searchInput || null : opts.initialFocus;

  const kit = createModal({
    title: opts.title,
    ...(opts.context != null ? { context: opts.context } : {}),
    ...(initialFocus !== undefined ? { initialFocus } : {}),
    // `size: null` is a STATEMENT (no size tier — the surface sizes itself, as
    // page selection does through maxWidth), so it must survive; only an
    // omitted size falls back to the wide tier.
    size: opts.size !== undefined ? opts.size : 'wide',
    maxWidth: opts.maxWidth,
    overlayClassName: opts.overlayClassName,
    className: opts.className,
    testid: opts.testid,
    withFooter: false,
    // A surface that owns its own focus restore across several entry points
    // (the SVG library restores to whichever control invoked it) opts out of
    // the kit's, and does it EXPLICITLY — the default stays the kit's.
    ...(opts.restoreFocus === false ? { restoreFocus: false } : {}),
    dismissOnBackdrop: opts.dismissOnBackdrop !== false,
    onEscape: (event) => {
      // Escape with a non-empty selection clears the selection instead of
      // closing — composed HERE because focus-trap dispatches trap-escape from
      // a document-level capture listener too; a keydown handler on the list
      // could never veto that path.
      if (list && list.getSelectedIds().length > 0) {
        list.clearSelection();
        return false;
      }
      if (typeof opts.onEscape === 'function') return opts.onEscape(event);
      return undefined;
    },
    onClose: () => list?.destroy()
  });

  list = mountCollectionList(kit.body, {
    ...opts,
    selbarHost: kit.modal,
    onRequestClose: () => kit.close(),
    apiExtras: {
      overlay: kit.overlay,
      modal: kit.modal,
      body: kit.body,
      // The header is a seam like `body` is — the detail stage mounts Back and
      // the surface's commit control onto it. The popover path has carried this
      // since the variant picker's detail stage moved there; the modal path
      // needed it the moment that pattern became the engine's.
      header: kit.header,
      setContext: kit.setContext
    }
  });
  return list;
}

/**
 * Where a popover-hosted collection lands.
 *
 *  · a function — the caller's own rect getter, passed through;
 *  · an element — the card sits BESIDE it (to its right, top edges aligned),
 *    which is what a picker that replaces one row of a list wants: the list
 *    stays where it was and the card opens next to the thing it acts on;
 *  · nothing, with a trigger — the primitive's own placement, below the trigger;
 *  · nothing at all — the frame's top-left gutter, which only a caller with no
 *    DOM to anchor to (the render harness) ever gets.
 *
 * A zero-size rect is deliberate: the primitive places a menu's top-left at
 * `rect.bottom`/`rect.left`, so a point IS the corner, and its scale-in
 * origin lands on that same point.
 */
function popoverAnchorRect(anchor, triggerEl) {
  if (typeof anchor === 'function') return anchor;
  if (anchor && typeof anchor.getBoundingClientRect === 'function') {
    return () => {
      const r = anchor.getBoundingClientRect();
      const x = r.right + POPOVER_BESIDE_GAP;
      const y = r.top - POPOVER_BESIDE_NUDGE;
      return { left: x, right: x, top: y, bottom: y, width: 0, height: 0 };
    };
  }
  if (triggerEl && typeof triggerEl.getBoundingClientRect === 'function') return undefined;
  return () => ({
    left: POPOVER_FALLBACK_INSET,
    right: POPOVER_FALLBACK_INSET,
    top: POPOVER_FALLBACK_INSET,
    bottom: POPOVER_FALLBACK_INSET,
    width: 0,
    height: 0
  });
}

/**
 * The popover form (2026-09-05) — the engine above, inside the shared floating
 * popover instead of a dialog. Same handle, same options, same three couplings
 * as `openCollectionBrowser`; the difference is the host.
 *
 * A picker that replaces ONE row of a list it sits beside is an errand, not a
 * residence: it opens next to the thing it acts on, takes no canvas, and the
 * list never moves under the pointer. The variant picker was that for its
 * whole life until SS-5 moved it into a dialog along with the chrome it
 * needed; this puts the chrome back where the surface lived. The header keeps
 * the dialog form's title, PP-1 context line and close button, so the card
 * reads as every other kit picker — floating.
 *
 * What the popover primitive brings, and this function does not re-implement:
 * fixed positioning clamped to the visual frame (and clear of a docked
 * panel), the focus trap, Escape, outside-click and canvas-click dismissal,
 * `aria-expanded` on the trigger, focus restore to it, and the phone sheet.
 * Its arrow-key walk is DISABLED — the kit owns grid key-nav.
 *
 * One thing the dialog form composes that this cannot: Escape-clears-selection.
 * The trap fires `trap-escape` from a document-level capture listener, so a
 * surface with a selection model should stay on the dialog form.
 *
 * @param {HTMLElement|null} triggerEl — owns aria-expanded, the outside-click
 *   exclusion and focus restore. May be null for a caller with no control.
 * @param {object} options — every `openCollectionBrowser` option, plus:
 *   `anchor` (element | rect getter), `width` (px, default 520), `align`
 *   ('left' default), `owner` (popover owner slot; default 'collection-popover'
 *   so a toolbar menu opening elsewhere does not tear it down), `dismiss`.
 * @returns {object|null} the list handle, or null when the testid guard refused
 */
export function openCollectionPopover(triggerEl, options) {
  const opts = options || {};
  if (typeof opts.load !== 'function') throw new Error('openCollectionPopover requires load()');
  if (typeof opts.adapt !== 'function') throw new Error('openCollectionPopover requires adapt()');

  // Same guard as the dialog form: an identical surface already open refuses
  // a second. The primitive removes its node synchronously on close (no
  // `exitAnimation` is passed), so there is no mid-close state to exempt.
  const guardSelector =
    opts.guardSelector ?? (opts.testid ? `[data-testid="${opts.testid}"]` : null);
  if (guardSelector && document.querySelector(guardSelector)) return null;

  let list = null;
  let contextEl = null;
  let headingEl = null;
  let menuEl = null;

  const initialFocus =
    opts.initialFocus === 'search' ? () => list?.searchInput || null : opts.initialFocus;

  /** The dialog form's `setContext`, for the surfaces that retarget mid-life. */
  function setContext(next) {
    if (!headingEl) return;
    if (contextEl) {
      contextEl.remove();
      contextEl = null;
      menuEl?.removeAttribute('aria-describedby');
    }
    const built = buildContextLine(next);
    if (!built) return;
    contextEl = built.el;
    contextEl.id = `pb-coll-popover-context-${++_popoverSeq}`;
    headingEl.appendChild(contextEl);
    menuEl?.setAttribute('aria-describedby', contextEl.id);
  }

  openPopoverMenu(triggerEl, {
    owner: opts.owner || 'collection-popover',
    role: 'dialog',
    className: ['pb-coll-popover', opts.className].filter(Boolean).join(' '),
    width: Number.isFinite(opts.width) ? opts.width : 520,
    align: opts.align || 'left',
    gap: 0,
    getAnchorRect: popoverAnchorRect(opts.anchor, triggerEl),
    disableArrowNav: true,
    phoneSheet: true,
    ...(initialFocus !== undefined ? { initialFocus } : {}),
    ...(opts.dismiss ? { dismiss: opts.dismiss } : {}),
    // A surface whose commit opens a MODAL over the card (the variant picker's
    // keep-edits question) needs a click inside that modal to not count as a
    // click outside the popover — or Cancel would dismiss both.
    ...(typeof opts.outsideClickFilter === 'function'
      ? { outsideClickFilter: opts.outsideClickFilter }
      : {}),
    ...(opts.restoreFocus === false ? { restoreFocus: () => {} } : {}),
    render: ({ close, menu }) => {
      menuEl = menu;
      if (opts.testid) menu.dataset.testid = opts.testid;

      const header = document.createElement('div');
      header.className = 'pb-coll-popover-header';
      headingEl = document.createElement('div');
      headingEl.className = 'pb-coll-popover-heading';
      if (opts.title) {
        const titleEl = document.createElement('h2');
        titleEl.className = 'pb-coll-popover-title';
        titleEl.id = `pb-coll-popover-title-${++_popoverSeq}`;
        titleEl.textContent = String(opts.title);
        headingEl.appendChild(titleEl);
        menu.setAttribute('aria-labelledby', titleEl.id);
      }
      if (opts.context != null) setContext(opts.context);
      // `headingSrOnly` — the title and its context line stay in the DOM and
      // stay the dialog's accessible name and description, and go visually
      // hidden. The SAME move the header takeover makes above
      // (`heading?.classList.add('pb-sr-only')`), and for the same reason: a
      // surface whose header says something more specific than its own title
      // should not also repeat the title, but dropping the node would leave the
      // dialog with no accessible name at all.
      if (opts.headingSrOnly) headingEl.classList.add('pb-sr-only');
      header.appendChild(headingEl);

      // `headerTrailing` — an OWNED control on the header row, right-aligned
      // before the close button. Same contract as `renderChipRow`'s `trailing`
      // slot and for the same reason: the header is built once per open here,
      // so a surface that appended to it itself would be appending to whichever
      // header happened to exist when its list shell was built.
      //
      // This is where a control that changes WHICH RECORDS EXIST belongs — the
      // chip row narrows the pool the dialog is showing, and a scope switch
      // decides what that pool is. Add Section makes the same split.
      const headerTrailing =
        typeof opts.headerTrailing === 'function' ? opts.headerTrailing() : null;
      if (headerTrailing) {
        // The kit's shared placement class (builder.css), not a popover-only
        // one: a control at the right edge of a dialog header is the same
        // problem in the modal form, and the modal's header solved it first.
        headerTrailing.classList.add('pb-dialog-header-trailing');
        header.appendChild(headerTrailing);
      }

      // The dialog's own close control, so the card carries the same glyph in
      // the same corner. `bare` + the kit class: no `.pb-btn` surface, the
      // modal's hover ring.
      const closeLabel = opts.closeLabel || 'Close';
      header.appendChild(
        createButton({
          variant: 'bare',
          className: 'pb-modal-close',
          ariaLabel: closeLabel,
          tooltip: closeLabel,
          content: iconEl('close'),
          onClick: () => close()
        })
      );

      const body = document.createElement('div');
      body.className = 'pb-coll-popover-body';

      list = mountCollectionList(body, {
        ...opts,
        selbarHost: menu,
        onRequestClose: () => close(),
        apiExtras: {
          overlay: null,
          modal: menu,
          body,
          // The header is a seam like `body` is. A surface whose DETAIL stage
          // owns header controls (the variant picker's Back / Use this variant)
          // would otherwise have to reach through `modal` and guess at the
          // kit's class names to find it.
          header,
          setContext
        }
      });

      const frag = document.createDocumentFragment();
      frag.append(header, body);
      return frag;
    },
    // The engine's own destroy() runs the surface's `onClose` (it is in
    // `...opts`); calling it here too would fire it twice.
    onClose: () => list?.destroy()
  });

  // The primitive hands back a no-op (and never ran `render`) when it had no
  // anchor rect at all — a detached trigger with no `anchor`. Nothing mounted.
  return list;
}
